import React, { useState } from 'react';
import { safeFormat, validateAndFormatDate } from '@/utils/date-helpers';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { Appointment } from '@/components/dashboard/client/types/appointmentTypes';

interface LateCancellationRequestModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
  onRequestSubmitted: () => void;
}

export function LateCancellationRequestModal({
  appointment,
  isOpen,
  onClose,
  onRequestSubmitted
}: LateCancellationRequestModalProps) {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Safely parse appointment date
  const appointmentDate = React.useMemo(() => {
    return validateAndFormatDate(appointment.start_time) || new Date();
  }, [appointment.start_time]);
  
  const appointmentEndDate = React.useMemo(() => {
    return validateAndFormatDate(appointment.end_time) || new Date();
  }, [appointment.end_time]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast({
        title: t('lateCancellationRequest.modal.errorTitle'),
        description: t('lateCancellationRequest.modal.fillAllFields'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit the late cancellation request
      const { error } = await supabase
        .from('late_cancellation_requests')
        .insert({
          booking_id: appointment.id,
          client_id: user?.id || '',
          practitioner_id: appointment.practitioner_id,
          client_reason: reason.trim(),
          status: 'pending'
        });

      if (error) {
        console.error('Error submitting late cancellation request:', error);
        toast({
          title: t('lateCancellationRequest.modal.errorTitle'),
          description: t('lateCancellationRequest.modal.submitError'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t('lateCancellationRequest.modal.requestSubmitted'),
        description: t('lateCancellationRequest.modal.requestSubmittedDesc'),
      });

      onRequestSubmitted();
      handleClose();
    } catch (error) {
      console.error('Error submitting late cancellation request:', error);
      toast({
        title: t('lateCancellationRequest.modal.errorTitle'),
        description: t('lateCancellationRequest.modal.submitError'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('lateCancellationRequest.modal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current appointment info */}
          <div className="border rounded-lg p-4 bg-muted/50">
            <h3 className="font-semibold mb-2">{t('lateCancellationRequest.modal.currentInfo')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{safeFormat(appointmentDate, 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{safeFormat(appointmentDate, 'h:mm a')} - {safeFormat(appointmentEndDate, 'h:mm a')}</span>
              </div>
              <div>
                <span className="font-medium">{t('lateCancellationRequest.modal.service')}: </span>
                <span>{appointment.service?.service_name}</span>
              </div>
              <div>
                <span className="font-medium">{t('lateCancellationRequest.modal.practitioner')}: </span>
                <span>{appointment.practitioner?.first_name} {appointment.practitioner?.last_name}</span>
              </div>
            </div>
          </div>

          {/* Warning notice */}
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('lateCancellationRequest.modal.warningTitle')}</AlertTitle>
            <AlertDescription className="text-sm">
              {t('lateCancellationRequest.modal.warningDescription')}
            </AlertDescription>
          </Alert>

          {/* Reason input */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-base font-semibold">
              {t('lateCancellationRequest.modal.reasonLabel')} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('lateCancellationRequest.modal.reasonPlaceholder')}
              className="min-h-[120px]"
            />
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('lateCancellationRequest.modal.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={!reason.trim() || isSubmitting}
            >
              {isSubmitting ? t('lateCancellationRequest.modal.submitting') : t('lateCancellationRequest.modal.submit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
